import { DiscussionType, User } from "@cnx/common";
import { Box, ScrollArea, TextInput } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import FormModal from "components/FormModal";
import UserAvatar from "components/UserAvatar";
import { filterUsers, getUserName } from "components/Users/helpers";
import UserInfo from "components/Users/ui/UserInfo";
import { useDiscussionsMutation } from "hooks/useDiscussions";
import { useUsers } from "hooks/useUsers";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "stores/authStore";

interface ModalNewPrivateDiscussionProps {
  onClose: () => void;
  onSelect: (discussionId: string) => void;
}

export const ModalNewPrivateDiscussion: React.FC<
  ModalNewPrivateDiscussionProps
> = ({ onClose, onSelect }) => {
  const { t } = useTranslation("discussions");
  const { users } = useUsers();
  const { mutateAsync, isPending } = useDiscussionsMutation();
  const currentUserId = useAuthStore(state => state.currentUserId);
  const [search, setSearch] = useState("");

  const filteredUsers = useMemo(() => {
    return users.filter(user =>
      filterUsers({
        user,
        filter: search,
        excludeIds: new Set([currentUserId ?? ""]),
      })
    );
  }, [users, currentUserId, search]);

  const handleSelectUser = async (user: User) => {
    const { data } = await mutateAsync({
      body: {
        discussionType: DiscussionType.PRIVATE,
        participants: [user.id!],
      },
    });
    onSelect(data.id);
    onClose();
  };

  return (
    <FormModal
      opened
      onSubmit={onClose}
      fullScreen
      onClose={onClose}
      zIndex={400}
      isPending={isPending}
      title={t("selectContact")}
      showSaveButton={false}
      xOffset={0}
      yOffset={0}
      yOffsetBottom={0}
      gap={0}
      fillHeight
      noContentScroll
    >
      <div className="flex flex-col h-full min-h-0">
        <TextInput
          placeholder={t("searchUsers")}
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={e => setSearch(e.currentTarget.value)}
          mx="sm"
          py="sm"
        />
        <ScrollArea style={{ flex: 1 }} scrollbarSize={4}>
          <div className="flex flex-col p-0 m-0">
            {filteredUsers.map(user => (
              <Box
                key={user.id}
                component="button"
                className="w-full px-4 py-4 border-b border-gray-light bg-white hover:bg-gray-lightest text-left flex flex-row items-center gap-3 cursor-pointer disabled:opacity-50"
                onClick={() => handleSelectUser(user)}
                disabled={isPending}
              >
                <UserAvatar
                  size={48}
                  avatarId={user.avatar ?? ""}
                  userName={getUserName({ ...user, initialsOnly: true })}
                />
                <UserInfo user={user} />
              </Box>
            ))}
          </div>
        </ScrollArea>
      </div>
    </FormModal>
  );
};
